/* eslint-disable jsx-a11y/anchor-is-valid */
import { FC, useEffect, useState } from 'react'
import { useIntl } from 'react-intl'
import { useQuery, useQueryClient } from 'react-query'
import { KTIcon } from '../../../_metronic/helpers'
import { PageLink, PageTitle, useLayout } from '../../../_metronic/layout/core'
import ModalEditRole from '../../../_metronic/partials/widgets/_new/modal/ModalEditRole'
import ModalViewRole from '../../../_metronic/partials/widgets/_new/modal/ModalViewRole'
import { axiosClientAuth } from '../../utils'
import { useAuth } from '../../modules/auth'

const ROLES_QUERY_KEY = 'roles'

const RolesManagement: FC = () => {
  const intl = useIntl()
  const [editRoleShow, setEditRoleShow] = useState(false)
  const [viewRoleShow, setViewRoleShow] = useState(false)
  const [currData, setCurrData] = useState<any>()
  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = useState(20)
  const { checkPermission } = useAuth()
  const queryClient = useQueryClient()

  const { data: rolesData, isLoading } = useQuery(
    [ROLES_QUERY_KEY, page, pageSize],
    () =>
      axiosClientAuth
        .get('/auth/roles/', { params: { page, page_size: pageSize } })
        .then((res) => res.data?.data),
    { keepPreviousData: true }
  )

  const data = rolesData?.results ?? []
  const totalPages = rolesData?.total_pages ?? 1

  const deleteRole = (id: number) => {
    // TODO: integrate new delete API
  }

  return (
    <>
      <div className='row g-5 g-xl-10 mb-5 mb-xl-10'>
        {isLoading ? (
          <div className='col-12 text-center py-10'>
            <div className='spinner-border text-primary' role='status'>
              <span className='visually-hidden'>Loading...</span>
            </div>
          </div>
        ) : data?.map((ele: any) => {
          const uniqueResources = Array.from(new Set((ele.permissions || []).map((p: any) => p.resource)))
          return (
          <div key={ele.id} className='col-md-4'>
            <div className='card card-flush h-md-100'>
              <div className='card-header'>
                <div className='card-title'>
                  <h2>{ele.name}</h2>
                </div>
              </div>
              <div className='card-body pt-1'>
                <div className='fw-bold text-gray-600 mb-3'>{ele.description}</div>
                <div className='d-flex flex-column text-gray-600'>
                  {uniqueResources.map((resource: any) => (
                    <div key={resource} className='d-flex align-items-center py-2'>
                      <span className='bullet bg-primary me-3' />
                      <span className='text-capitalize'>{resource.replace(/_/g, ' ')}</span>
                    </div>
                  ))}
                </div>
              </div>
              <div className='card-footer flex-wrap pt-0'>
                <button
                  type='button'
                  className='btn btn-light btn-active-primary my-1 me-2'
                  onClick={() => {
                    setCurrData(ele)
                    setViewRoleShow(true)
                  }}
                >
                  {intl.formatMessage({ id: 'COMMON.BUTTON.VIEW_ROLE', defaultMessage: 'View Role' })}
                </button>
                {checkPermission('Roles Management', 'can_edit') && (
                  <>
                    <button
                      type='button'
                      className='btn btn-light btn-active-light-primary me-2 my-1'
                      onClick={() => {
                        setCurrData(ele)
                        setEditRoleShow(true)
                      }}
                    >
                      {intl.formatMessage({ id: 'COMMON.BUTTON.EDIT_ROLE', defaultMessage: 'Edit Role' })}
                    </button>
                    <button
                      type='button'
                      className='btn btn-light btn-active-primary my-1 me-2'
                      onClick={() => deleteRole(ele?.id)}
                    >
                      {intl.formatMessage({ id: 'COMMON.BUTTON.DELETE', defaultMessage: 'Delete' })}
                    </button>
                  </>
                )}
              </div>
            </div>
          </div>
          )
        })}
      </div>

      <ModalEditRole
        show={editRoleShow}
        setShow={setEditRoleShow}
        curr={currData}
        onSuccess={() => queryClient.invalidateQueries([ROLES_QUERY_KEY])}
      />
      <ModalViewRole show={viewRoleShow} setShow={setViewRoleShow} curr={currData} />

      <div className='d-flex justify-content-between align-items-center mt-5'>
          <div>
            <select
              className='form-select form-select-sm form-select-solid'
              value={pageSize}
              onChange={(e) => { setPageSize(parseInt(e.target.value)); setPage(1) }}
            >
              {[10, 20, 25, 50].map((n) => (
                <option key={n} value={n}>{n}</option>
              ))}
            </select>
          </div>
          <div>
            <span>Page <strong>{page} of {totalPages}</strong></span>
          </div>
          <div>
            <button
              className='btn btn-sm'
              onClick={() => setPage((p) => p - 1)}
              disabled={page <= 1}
            >
              <KTIcon iconName='left' className='text-gray fs-1 me-5' />
            </button>
            <button
              className='btn btn-sm'
              onClick={() => setPage((p) => p + 1)}
              disabled={page >= totalPages}
            >
              <KTIcon iconName='right' className='text-gray fs-1 me-5' />
            </button>
          </div>
        </div>
    </>
  )
}

const myBreadCrumbs: Array<PageLink> = [
  {
    title: 'Home',
    path: '/dashboard',
    isSeparator: false,
    isActive: false,
  },
  {
    title: '',
    path: '',
    isSeparator: true,
    isActive: false,
  },
]

const RolesManagementWrapper: FC = () => {
  const intl = useIntl()
  const { setRightTolbar } = useLayout()

  useEffect(() => {
    setRightTolbar('rolesmanagement')
  }, [])

  return (
    <>
      <PageTitle breadcrumbs={myBreadCrumbs}>{intl.formatMessage({ id: 'PAGE.ROLES_MANAGEMENT', defaultMessage: 'Roles Management' })}</PageTitle>
      <RolesManagement />
    </>
  )
}

export { RolesManagementWrapper }
